import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cx } from '../primitives/cx.js';
import { Icon } from '../primitives/Icon.tsx';
import { Button, IconButton, InlineAction } from '../primitives/Button.tsx';
import { Num } from '../primitives/Num.tsx';

/**
 * The activity queue (F-155), and the interaction the whole model rests on.
 *
 * Completing a step opens a popover that logs the outcome AND carries the next
 * step's fields, already filled in from the step type's usual interval. The
 * primary button completes and schedules. Doing nothing but pressing it
 * schedules.
 *
 * Leaving a record with nothing scheduled is possible and is a *choice*: a
 * separate control, which then asks for a reason. It is never what happens by
 * default, never a toast offering to schedule afterwards, and never a second
 * dialog somebody can dismiss. If a rep can complete a step and leave the
 * record empty by doing nothing, the queue drains over a fortnight and the
 * model is dead - so the component is built so that the lazy path is the
 * correct one.
 */

export type DueSection = 'overdue' | 'today' | 'this-week' | 'later';

export interface QueueStep {
  id: string;
  typeId: string;
  typeLabel: string;
  typeIcon: string;
  /** Already formatted for display by the one formatter. */
  dueLabel: string;
  section: DueSection;
  note: string | null;
  recordName: string | null;
  recordHref: string | null;
  /** A second line of context: the company, the amount, whatever matters. */
  recordContext: string | null;
}

export interface StepType {
  typeId: string;
  label: string;
  icon: string;
  defaultDelayDays: number;
  requiresNote: boolean;
}

export interface CompletionSubmission {
  stepId: string;
  outcome: string;
  note: string | null;
  next: { schedule: true; typeId: string; dueDate: string; note: string | null } | { schedule: false; reason: string };
}

export interface ActivityQueueLabels {
  sections: Record<DueSection, string>;
  complete: string;
  snooze: string;
  reassign: string;
  /** Popover. */
  howDidItGo: string;
  outcome: string;
  note: string;
  andWhatIsNext: string;
  nextStepType: string;
  nextStepWhen: string;
  submitScheduling: string;
  submitWithoutScheduling: string;
  chooseNothingNext: string;
  keepScheduling: string;
  nothingNextWarning: string;
  nothingNextReason: string;
  cancel: string;
  outcomes: string[];
}

export interface ActivityQueueProps {
  steps: QueueStep[];
  types: StepType[];
  labels: ActivityQueueLabels;
  /** Turns a number of days from today into a calendar date, in the workspace zone. */
  dueDateIn: (days: number) => string;
  onComplete: (submission: CompletionSubmission) => Promise<void> | void;
  onSnooze: (stepId: string, dueDate: string) => void;
  /**
   * Optional on purpose. Passing a handler that does nothing would render a
   * button that looks like every other button and answers to nothing when
   * pressed, which is worse than not offering it. Until there is a way to
   * choose a person, the control is not there.
   */
  onReassign?: ((stepId: string) => void) | undefined;
  /** Rendered when there is nothing scheduled at all. */
  empty: ReactNode;
}

const SECTION_ORDER: DueSection[] = ['overdue', 'today', 'this-week', 'later'];

export function ActivityQueue({
  steps,
  types,
  labels,
  dueDateIn,
  onComplete,
  onSnooze,
  onReassign,
  empty,
}: ActivityQueueProps): JSX.Element {
  const [completing, setCompleting] = useState<QueueStep | null>(null);

  if (steps.length === 0) {
    return <div data-component="ActivityQueue">{empty}</div>;
  }

  return (
    <div data-component="ActivityQueue" className="flex flex-col gap-6">
      {SECTION_ORDER.map((section) => {
        const inSection = steps.filter((step) => step.section === section);
        if (inSection.length === 0) return null;
        return (
          <section key={section} data-due-section={section} className="flex flex-col gap-2">
            <h2
              data-due-section-heading={section}
              className={cx(
                'flex items-center gap-2 text-micro uppercase tracking-label',
                section === 'overdue' ? 'text-danger' : 'text-ink-label',
              )}
            >
              {labels.sections[section]}
              <Num className="text-ink-meta">{String(inSection.length)}</Num>
            </h2>
            <ul className="flex flex-col">
              {inSection.map((step) => (
                <li key={step.id} className="border-t border-t-rule first:border-t-0">
                  <QueueRow
                    step={step}
                    labels={labels}
                    onComplete={() => setCompleting(step)}
                    onSnooze={() => onSnooze(step.id, dueDateIn(1))}
                    onReassign={onReassign ? () => onReassign(step.id) : undefined}
                  />
                </li>
              ))}
            </ul>
          </section>
        );
      })}

      {completing ? (
        <CompleteStepPopover
          step={completing}
          types={types}
          labels={labels}
          dueDateIn={dueDateIn}
          onCancel={() => setCompleting(null)}
          onSubmit={async (submission) => {
            await onComplete(submission);
            setCompleting(null);
          }}
        />
      ) : null}
    </div>
  );
}

function QueueRow({
  step,
  labels,
  onComplete,
  onSnooze,
  onReassign,
}: {
  step: QueueStep;
  labels: ActivityQueueLabels;
  onComplete: () => void;
  onSnooze: () => void;
  onReassign?: (() => void) | undefined;
}): JSX.Element {
  return (
    <div
      data-component="QueueRow"
      className="flex min-h-touch flex-wrap items-center gap-3 py-3"
    >
      <span className="text-ink-400">
        <Icon name={step.typeIcon} decorative />
      </span>

      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-body text-ink">{step.typeLabel}</span>
        <span className="flex min-w-0 flex-wrap items-center gap-2 text-meta text-ink-meta">
          {step.recordHref && step.recordName ? (
            <a href={step.recordHref} className="truncate hover:text-bronze-text">
              {step.recordName}
            </a>
          ) : (
            <span className="truncate">{step.recordName ?? ''}</span>
          )}
          {step.recordContext ? <Num>{step.recordContext}</Num> : null}
        </span>
      </span>

      {/*
        Under "Today" the row's own due label says "today", which the heading
        directly above it already said. Everywhere else it carries something
        the heading does not - how overdue, which weekday, which date - so it
        stays. Repeating a heading on every row costs a column and tells the
        reader nothing.
      */}
      {step.section === 'today' ? null : (
        <Num
          className={cx('text-meta', step.section === 'overdue' ? 'text-danger' : 'text-ink-meta')}
        >
          {step.dueLabel}
        </Num>
      )}

      <span className="flex items-center gap-1">
        <InlineAction onClick={onComplete}>{labels.complete}</InlineAction>
        <IconButton icon="Clock" label={labels.snooze} onClick={onSnooze} />
        {onReassign ? (
          <IconButton icon="UserGear" label={labels.reassign} onClick={onReassign} />
        ) : null}
      </span>
    </div>
  );
}

/**
 * The completion popover.
 *
 * One form, two halves: what happened, and what is next. The second half is
 * filled in before anybody types - the step type carries its usual interval,
 * so the ordinary case costs no interaction at all.
 */
export function CompleteStepPopover({
  step,
  types,
  labels,
  dueDateIn,
  onCancel,
  onSubmit,
}: {
  step: QueueStep;
  types: StepType[];
  labels: ActivityQueueLabels;
  dueDateIn: (days: number) => string;
  onCancel: () => void;
  onSubmit: (submission: CompletionSubmission) => Promise<void> | void;
}): JSX.Element {
  const defaultType = types.find((type) => type.typeId === step.typeId) ?? types[0];
  const [outcome, setOutcome] = useState(labels.outcomes[0] ?? '');
  const [note, setNote] = useState('');
  const [nextTypeId, setNextTypeId] = useState(defaultType?.typeId ?? '');
  const [nextDate, setNextDate] = useState(() => dueDateIn(defaultType?.defaultDelayDays ?? 3));
  const [nextNote, setNextNote] = useState('');
  // Scheduling is the state this form starts in. Choosing otherwise is an act.
  const [scheduling, setScheduling] = useState(true);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const dialog = useRef<HTMLDivElement>(null);

  // Changing the step type moves the date to that type's usual interval, so
  // the two never disagree without somebody having chosen it.
  useEffect(() => {
    const type = types.find((candidate) => candidate.typeId === nextTypeId);
    if (type) setNextDate(dueDateIn(type.defaultDelayDays));
  }, [nextTypeId, types, dueDateIn]);

  useEffect(() => {
    dialog.current?.querySelector<HTMLElement>('select, input, button')?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const selectedType = types.find((type) => type.typeId === nextTypeId);
  const noteMissing = scheduling && selectedType?.requiresNote === true && nextNote.trim() === '';
  const reasonMissing = !scheduling && reason.trim() === '';

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-canvas/80 p-5">
      <div
        ref={dialog}
        data-component="CompleteStepPopover"
        role="dialog"
        aria-modal="true"
        aria-label={labels.howDidItGo}
        className="flex w-full max-w-modal-confirm flex-col gap-4 rounded-card bg-surface p-5 shadow-modal"
      >
        <h2 className="text-lead font-medium text-ink">{labels.howDidItGo}</h2>
        <p className="text-meta text-ink-meta">
          {step.typeLabel}
          {step.recordName ? ` · ${step.recordName}` : ''}
        </p>

        <label className="flex flex-col gap-1">
          <span className="text-dense font-medium text-ink">{labels.outcome}</span>
          <select
            value={outcome}
            onChange={(event) => setOutcome(event.target.value)}
            className="h-input rounded-control border border-control bg-surface px-input-x text-body text-ink"
          >
            {labels.outcomes.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-dense font-medium text-ink">{labels.note}</span>
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={2}
            className="rounded-control border border-control bg-surface p-3 text-body text-ink"
          />
        </label>

        {/*
          The second half of the same form. Not a second dialog, not a toast
          afterwards: the next step is decided here or it is deliberately
          declined here.
        */}
        <div className="flex flex-col gap-3 rounded-card bg-sunken p-4">
          <h3 className="text-dense font-medium text-ink">{labels.andWhatIsNext}</h3>

          {scheduling ? (
            <>
              <div className="flex flex-wrap gap-3">
                <label className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="text-meta text-ink-meta">{labels.nextStepType}</span>
                  <select
                    value={nextTypeId}
                    onChange={(event) => setNextTypeId(event.target.value)}
                    className="h-input rounded-control border border-control bg-surface px-input-x text-body text-ink"
                  >
                    {types.map((type) => (
                      <option key={type.typeId} value={type.typeId}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="text-meta text-ink-meta">{labels.nextStepWhen}</span>
                  <input
                    type="date"
                    value={nextDate}
                    onChange={(event) => setNextDate(event.target.value)}
                    className="h-input rounded-control border border-control bg-surface px-input-x text-body text-ink"
                  />
                </label>
              </div>
              {selectedType?.requiresNote ? (
                <label className="flex flex-col gap-1">
                  <span className="text-meta text-ink-meta">{labels.note}</span>
                  <input
                    value={nextNote}
                    onChange={(event) => setNextNote(event.target.value)}
                    className="h-input rounded-control border border-control bg-surface px-input-x text-body text-ink"
                  />
                </label>
              ) : null}
              <button
                type="button"
                data-choose-nothing-next
                onClick={() => setScheduling(false)}
                className="self-start text-meta text-ink-meta underline decoration-rule underline-offset-4"
              >
                {labels.chooseNothingNext}
              </button>
            </>
          ) : (
            <>
              <p className="flex items-start gap-2 text-dense text-warning">
                <Icon name="Warning" size="inline" decorative className="mt-px shrink-0" />
                <span>{labels.nothingNextWarning}</span>
              </p>
              <label className="flex flex-col gap-1">
                <span className="text-meta text-ink-meta">{labels.nothingNextReason}</span>
                <input
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  className="h-input rounded-control border border-control bg-surface px-input-x text-body text-ink"
                />
              </label>
              <button
                type="button"
                onClick={() => setScheduling(true)}
                className="self-start text-meta text-bronze-text underline decoration-bronze-rule underline-offset-4"
              >
                {labels.keepScheduling}
              </button>
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            intent="primary"
            busy={busy}
            disabled={noteMissing || reasonMissing}
            onClick={() => {
              setBusy(true);
              void Promise.resolve(
                onSubmit({
                  stepId: step.id,
                  outcome,
                  note: note.trim() ? note.trim() : null,
                  next: scheduling
                    ? {
                        schedule: true,
                        typeId: nextTypeId,
                        dueDate: nextDate,
                        note: nextNote.trim() ? nextNote.trim() : null,
                      }
                    : { schedule: false, reason: reason.trim() },
                }),
              ).finally(() => setBusy(false));
            }}
          >
            {scheduling ? labels.submitScheduling : labels.submitWithoutScheduling}
          </Button>
          <Button intent="ghost" onClick={onCancel}>
            {labels.cancel}
          </Button>
        </div>
      </div>
    </div>
  );
}
